import { z } from 'zod';

/**
 * Validates a single email domain — labels separated by dots, each label
 * 1-63 chars of letters/digits/hyphens (not starting or ending with a
 * hyphen), with a minimum of two labels and a 2+ char top-level label.
 *
 * Examples that match:    runloop.ai, sub.runloop.ai, my-org.example.com
 * Examples that fail:     @runloop.ai, runloop, runloop., -runloop.ai, ai
 */
const DOMAIN_REGEX = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-z0-9-]{1,63}(?<!-))+$/;

export const EmailDomainSchema = z
  .string()
  .trim()
  .min(1, 'Domain is required')
  .max(253, 'Domain is too long')
  .regex(DOMAIN_REGEX, 'Enter a valid domain (e.g. runloop.ai). Do not include the leading "@".')
  .transform((value) => value.toLowerCase());

/**
 * Validates a single email address. Accepted entries are trimmed and
 * lower-cased so matching against an incoming OAuth email is a plain
 * string equality check against the stored list.
 *
 * Backed by Zod's built-in email validator — the same shape the OAuth
 * resolver normalises through, so paste-from-spreadsheet entries with
 * stray whitespace or uppercase characters round-trip cleanly.
 */
export const EmailAddressSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .max(254, 'Email is too long')
  .email('Enter a valid email address (e.g. alex@runloop.ai).')
  .transform((value) => value.toLowerCase());

/**
 * Tri-state controlling how `allowedEmailDomains` and `allowedEmails`
 * are interpreted:
 *
 *  - `off`   — both lists are ignored, every email is allowed
 *  - `allow` — sign-in is restricted to the union of the two lists
 *  - `deny`  — sign-in is forbidden for anything in either list
 */
export const AuthAllowlistModeSchema = z.enum(['off', 'allow', 'deny']);
export type AuthAllowlistMode = z.infer<typeof AuthAllowlistModeSchema>;

export const AuthSettingsSchema = z.object({
  /**
   * Lower-cased list of email domains. Treated as an allowlist or
   * denylist depending on `mode`; ignored when `mode === 'off'`.
   */
  allowedEmailDomains: z.array(EmailDomainSchema),
  /**
   * Lower-cased list of individual email addresses. Treated as an
   * allowlist or denylist depending on `mode`, in addition to (and with
   * the same semantics as) `allowedEmailDomains`. Lets admins onboard
   * one-off users (contractors, vendors) without having to allow their
   * entire email provider. Ignored when `mode === 'off'`.
   */
  allowedEmails: z.array(EmailAddressSchema).default([]),
  /** Interpretation of `allowedEmailDomains` + `allowedEmails`. See {@link AuthAllowlistModeSchema}. */
  mode: AuthAllowlistModeSchema,
  /**
   * When `false`, ordinary users can no longer create their own
   * organizations through the self-serve flow (`POST /organizations`);
   * the only way to provision an org is a platform admin creating one from
   * the Admin → Organizations page. Defaults to `true` so existing
   * deployments keep their open self-serve signup behaviour. Platform
   * admins are never blocked regardless of this setting.
   */
  allowSelfServeOrgCreation: z.boolean().default(true),
  updatedAt: z.number(),
  updatedBy: z.string().nullable(),
});
export type AuthSettings = z.infer<typeof AuthSettingsSchema>;

export const UpdateAuthSettingsRequestSchema = z.object({
  allowedEmailDomains: z.array(EmailDomainSchema),
  /**
   * Optional for backward compatibility: clients that pre-date the
   * per-email allowlist keep working — they implicitly preserve
   * whatever list is currently saved. New clients should always send
   * a value (possibly an empty array) to make their intent explicit.
   */
  allowedEmails: z.array(EmailAddressSchema).optional(),
  /**
   * Optional for backward compatibility: existing clients that pre-date the
   * tri-state mode keep working — they implicitly preserve whatever mode is
   * currently saved. New clients should always send a value to make their
   * intent explicit.
   */
  mode: AuthAllowlistModeSchema.optional(),
  /**
   * Optional for backward compatibility: clients that pre-date the
   * self-serve org-creation toggle keep working — omitting it preserves
   * whatever value is currently saved.
   */
  allowSelfServeOrgCreation: z.boolean().optional(),
});
export type UpdateAuthSettingsRequest = z.infer<typeof UpdateAuthSettingsRequestSchema>;
